import { Box, Button, IconButton, Typography } from '@mui/material';
import _ from 'lodash';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import '../../../../assets/style/sass/profile.scss';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { popupActions } from '../../../../redux/slices/onboardingPopupsSlice';
import { fetchUserEducation } from '../../../../redux/slices/user-education/userEducationSlice';
import { disabilityPopupActions } from '../../../../redux/slices/disability/disabilityPopupSlice';
import { fetchUserWorkHistory } from '../../../../redux/slices/user-work/userWorkHistorySlice';
import { getUserDetails } from '../../../../utils/orgName';
import { camelCaseToCapital } from '../../../../utils/topicsMapping';
import { camelCaseToCapitalIndustries } from '../../../../utils/industriesMapping';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import useResponsiveScreenSwitch from '../../../../utils/useResponsiveScreenSwitch';
import { EditIcon } from '../../../../utils/Icons';
import MyChip from '../../components/MyChip';
import RoleTypeTag from '../../components/RoleTypeTag';
import WorkAndEduDesign from '../../components/WorkAndEduDesign';
import VideoPlayer from '../../components/VideoPlayer';
import EditProfile from '../../components/EditProfile';
import { GetSocialIcon } from '../../components/SocialIcon';
import { useLoader } from '../../components/useLoader';
import { ProfilePageLoader } from '../../../../Loader/ProfilePageLoader';
import DisabilityModal from '../../components/DisabilityModal';
import SocialHandlesModal from '../../components/SocialHandlesModal';
import HobbiesModal from '../../components/HobbiesModal';
import DigitalIntroModal from '../../components/DigitalIntroModal';
import EducationModal from '../../components/EducationModal';
import WorkHistoryModal from '../../components/WorkHistoryModal';
import MemberAttributesModal from '../../components/MemberAttributesModal';
import { NO_ROLE_MESSAGE, teamsWithoutRole } from '../../utils/teamRoles';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { useCapabilityReleaseForMembers } from '../../../../hooks/useCapabilityRelease';
import useNoRoleViewer from '../../../../hooks/useNoRoleViewer';
import type { OrgMemberAttribute } from '../../components/MemberAttributesModal';
import {
  userMemberAttributesUpdate,
  userMemberAttributesChangedUpdate,
  userMemberAttributesAckUpdate,
  userMemberAttributesDeclinedUpdate
} from '../../../../redux/slices/user-profile/userProfieSlice';
import { API } from '../../../../api';
import { useIsMobileApp } from '../../../../mobile/mobileApp';
import {
  catalogValueIds,
  filterHiddenMemberAttributes,
  normalizeOrgCatalog,
  resolveMemberAttributeRows,
  unwrapEnvelope,
  type MemberAttributeProfileRow
} from '../../utils/memberAttributes';

interface ProfileCardProps {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}

/**
 * The Personal Information photo. Shows the saved headshot, and falls back to
 * the initial when there is none or it fails to load. The URL changes on every
 * upload, so the failed flag resets and a new photo shows as soon as it's saved.
 */
const ProfileAvatar: React.FC<{ headshot?: string; initials: string }> = ({ headshot, initials }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [headshot]);

  if (!headshot || failed) {
    return <Box className="profileAvatarSquare">{initials}</Box>;
  }
  return (
    <Box className="profileAvatarSquare profileAvatarSquare--photo">
      <img src={headshot} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    </Box>
  );
};

const ProfileCard = ({ title, children, action }: ProfileCardProps) => (
  <Box className="profileCard">
    <Box className="profileCardHeader">
      <Typography className="profileCardTitle">{title}</Typography>
      <Box className="profileCardHeaderAction">
        {action ?? (
          <div className="profileCardHeaderActionIcon">
            <div className="profileCardHeaderActionIconInner" />
          </div>
        )}
      </Box>
    </Box>
    <Box className="profileCardBody">{children}</Box>
  </Box>
);

const Profile = () => {
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.userProfile);
  const data = userDetails.data?.personal_details;
  const userBio = data?.bio;
  const { forMobile, mobileBackBtn } = useResponsiveScreenSwitch();
  // The phone shell's header already has a back button.
  const isMobileApp = useIsMobileApp();
  const { videoIntro } = useProfileDetails();
  const { userId, orgId } = getUserDetails();
  const showLoader = useLoader(userDetails.loading, 1000);
  const workHistoryState = useAppSelector((state) => state.userWorkHistory);
  const educationState = useAppSelector((state) => state.userEducation);
  const [learningObjectives, setLearningObjectives] = useState<string[]>([]);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [memberAttributesModalOpen, setMemberAttributesModalOpen] = useState(false);
  const [fetchedOrgMemberAttributes, setFetchedOrgMemberAttributes] = useState<OrgMemberAttribute[]>([]);
  const [orgAttributesForLabels, setOrgAttributesForLabels] = useState<OrgMemberAttribute[]>([]);

  const workHistoryData = workHistoryState.updatedData || [];
  const educationData = educationState.updatedData || [];

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserWorkHistory(userId));
      dispatch(fetchUserEducation(userId));
    }
  }, [dispatch, userId]);

  // Org attribute catalog, used to turn the ids on `users.memberAttributes`
  // into the labels the "My Attributes" card shows. Most profile payloads
  // carry it inline; when they don't we fetch it the same way the attributes
  // prompt does (API.getGroupMemberAttributes against one of the member's
  // groups) rather than reading it from anywhere else.
  useEffect(() => {
    if (!userDetails.data || !orgId) return;
    const fromProfile = userDetails.data?.orgMemberAttributes ?? [];
    if (Array.isArray(fromProfile) && fromProfile.length > 0) {
      setOrgAttributesForLabels(fromProfile);
      return;
    }
    let cancelled = false;
    const fetchAttributesByGrpId = (grpId: string) => {
      API.getGroupMemberAttributes({ grpId })
        .then((res) => {
          if (cancelled) return;
          const body = unwrapEnvelope(res) as { orgMemberAttributes?: OrgMemberAttribute[] };
          const list = body?.orgMemberAttributes;
          setOrgAttributesForLabels(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setOrgAttributesForLabels([]);
        });
    };
    const grpIdFromProfile =
      userDetails.data?.groups?.[0] || userDetails.data?.groupsAll?.[0];
    if (grpIdFromProfile) {
      fetchAttributesByGrpId(grpIdFromProfile);
      return () => {
        cancelled = true;
      };
    }
    API.groupsListForUser(orgId)
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        const groups = Array.isArray(data) ? data : [];
        const firstGroupId = groups[0]?.id ?? groups[0]?._id;
        if (firstGroupId) fetchAttributesByGrpId(firstGroupId);
        else setOrgAttributesForLabels([]);
      })
      .catch(() => {
        if (!cancelled) setOrgAttributesForLabels([]);
      });
    return () => {
      cancelled = true;
    };
    // `userDetails.data` is in the deps because the guard above reads it: a
    // profile that ships neither `orgMemberAttributes` nor `groups` leaves
    // every other dep at `undefined` both before and after it loads, so
    // without this the fetch would never run for exactly the members who
    // need it.
  }, [
    orgId,
    userDetails.data,
    userDetails.data?.orgMemberAttributes,
    userDetails.data?.groups,
    userDetails.data?.groupsAll
  ]);

  useEffect(() => {
    if (!memberAttributesModalOpen) {
      setFetchedOrgMemberAttributes([]);
      return;
    }
    const fromProfile = userDetails.data?.orgMemberAttributes ?? [];
    if (Array.isArray(fromProfile) && fromProfile.length > 0) {
      setFetchedOrgMemberAttributes(fromProfile);
      return;
    }

    let cancelled = false;

    const fetchAttributesByGrpId = (grpId: string) => {
      API.getGroupMemberAttributes({ grpId })
        .then((res) => {
          if (cancelled) return;
          const body = unwrapEnvelope(res) as { orgMemberAttributes?: OrgMemberAttribute[] };
          const list = body?.orgMemberAttributes;
          setFetchedOrgMemberAttributes(Array.isArray(list) ? list : []);
        })
        .catch(() => {
          if (!cancelled) setFetchedOrgMemberAttributes([]);
        });
    };

    const grpIdFromProfile =
      userDetails.data?.groups?.[0] || userDetails.data?.groupsAll?.[0];
    if (grpIdFromProfile) {
      fetchAttributesByGrpId(grpIdFromProfile);
      return () => {
        cancelled = true;
      };
    }

    if (!orgId) {
      setFetchedOrgMemberAttributes([]);
      return;
    }
    API.groupsListForUser(orgId)
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data?.data ?? (res as any)?.data;
        const groups = Array.isArray(data) ? data : [];
        const firstGroupId = groups[0]?.id ?? groups[0]?._id;
        if (firstGroupId) {
          fetchAttributesByGrpId(firstGroupId);
        } else {
          setFetchedOrgMemberAttributes([]);
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedOrgMemberAttributes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    memberAttributesModalOpen,
    orgId,
    userDetails.data?.orgMemberAttributes,
    userDetails.data?.groups,
    userDetails.data?.groupsAll
  ]);

  useEffect(() => {
    const rawObjectives = userDetails.data?.learningObjectives;
    const parsedObjectives: string[] = [];

    const collectObjectives = (value: any) => {
      if (!value) return;
      if (typeof value === 'string') {
        parsedObjectives.push(value);
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => collectObjectives(item));
        return;
      }
      if (typeof value === 'object') {
        const label = value.objective || value.name || value.title || value.label || value.value;
        if (typeof label === 'string' && label.trim().length > 0) {
          parsedObjectives.push(label);
          return;
        }
        Object.values(value).forEach((item) => collectObjectives(item));
      }
    };

    collectObjectives(rawObjectives);
    const uniqueObjectives = [...new Set(parsedObjectives.map((item) => item.trim()).filter(Boolean))];
    setLearningObjectives(uniqueObjectives);
  }, [userDetails.data?.learningObjectives]);

  const topicChips = useMemo(() => {
    const topicLabels = [
      ...camelCaseToCapitalIndustries(userDetails.data?.selectedIndustries || []),
      ...(camelCaseToCapital(userDetails.data?.selectedTopics) || [])
    ];
    return _.sortBy(topicLabels);
  }, [userDetails.data]);

  /**
   * "My Attributes" — one row per org attribute list, resolved to labels.
   *
   * `users.memberAttributes` is a flat array of value ids, so nothing here is
   * readable without the org catalog. The catalog comes from the profile
   * payload when it carries one and from the fetch above otherwise: note the
   * `.length` test rather than `??`, because a profile that ships
   * `orgMemberAttributes: []` is exactly the case the fetch exists for and
   * `[] ?? fetched` keeps the empty array — that was the bug, the fetched
   * catalog was loaded and then thrown away, leaving every id unresolved.
   */
  // "Role in <team>" only when the Capability release is on for members (not in admin mode).
  const forMembers = useCapabilityReleaseForMembers();
  const memberTeamRoles = forMembers ? userDetails.data?.teamRoles ?? null : null;
  const memberAttributeRows = useMemo(() => {
    const fromProfile = userDetails.data?.orgMemberAttributes ?? [];
    const orgAttrs = fromProfile.length > 0 ? fromProfile : orgAttributesForLabels;
    const catalog = normalizeOrgCatalog(filterHiddenMemberAttributes(orgAttrs), undefined);
    // Declined lists (`users.memberAttributesDeclinedLists`) are not on the
    // profile payload yet; pass them here once they are and empty rows read
    // "None" on their own.
    return resolveMemberAttributeRows(catalog, userDetails.data?.memberAttributes ?? [], undefined);
  }, [userDetails.data?.memberAttributes, userDetails.data?.orgMemberAttributes, orgAttributesForLabels]);

  // The member's teams with roles set up in which they hold none (utils/teamRoles.ts, the same rule as
  // the mark in the left navigation): their pills carry the urgent mark, with what to do on hover. Never
  // an org admin's, nor a team they manage (useNoRoleViewer; none until that is known).
  const noRoleViewer = useNoRoleViewer();
  const flaggedTeams = useMemo(() => {
    if (!noRoleViewer) return new Set<string>();
    const fromProfile = userDetails.data?.orgMemberAttributes ?? [];
    const lists = filterHiddenMemberAttributes(fromProfile.length > 0 ? fromProfile : orgAttributesForLabels);
    const picked = userDetails.data?.memberAttributes ?? [];
    return new Set(
      teamsWithoutRole(memberTeamRoles, lists as Parameters<typeof teamsWithoutRole>[1], picked, noRoleViewer).map(
        (t) => t.name
      )
    );
  }, [
    noRoleViewer,
    memberTeamRoles,
    userDetails.data?.memberAttributes,
    userDetails.data?.orgMemberAttributes,
    orgAttributesForLabels
  ]);

  const handleOpenSocial = () => {
    dispatch(popupActions.updatePopups({ social: true }));
  };

  const handleOpenHobbies = () => {
    dispatch(popupActions.updatePopups({ hobbies: true }));
  };

  const handleOpenDigital = () => {
    if (videoIntro?.includes('blob:')) {
      toast.warning('Upload is in progress');
      return;
    }
    dispatch(popupActions.updatePopups({ digitalIntro: true }));
  };

  const handleAddWorkHistory = () => {
    dispatch(popupActions.updatePopups({ workHistory: { open: true, action: 'Add', item: {} } }));
  };

  const handleAddEducation = () => {
    dispatch(popupActions.updatePopups({ education: { open: true, action: 'Add', item: {} } }));
  };

  const handleEditWorkHistory = (item: any) => {
    dispatch(popupActions.updatePopups({ workHistory: { open: true, action: 'Edit', item } }));
  };

  const handleEditEducation = (item: any) => {
    dispatch(popupActions.updatePopups({ education: { open: true, action: 'Edit', item } }));
  };

  const handleOpenDisability = () => {
    dispatch(disabilityPopupActions.handleDisabilityPopup({ open: true, disable: false }));
  };

  const outlineIcon = (
    <div className="profileCardHeaderActionIcon">
      <div className="profileCardHeaderActionIconInner" />
    </div>
  );

  const renderChips = (items: string[], fallback: string) => {
    if (items.length === 0) {
      return <Typography className="profilePlaceholderText">{fallback}</Typography>;
    }
    return (
      <Box className="profileChipRow">
        {items.map((item, index) => (
          <MyChip
            key={`${item}-${index}`}
            label={item}
            color="var(--Text-text-primary, #FDFDFD)"
            backgroundColor="transparent"
            border="1px solid var(--Stroke-Border-border, #444444)"
            fontSize={12}
          />
        ))}
      </Box>
    );
  };

  /**
   * "My Attributes" body: one row per org attribute list, its name on the left
   * and the member's answers as chips — the same chip treatment the rest of
   * the page uses, so it reads as "Department: Design".
   *
   * A list the member holds no value in keeps its row and shows a placeholder,
   * so the card says which questions are still open instead of silently
   * dropping them. Once `memberAttributesDeclinedLists` lands, a declined list
   * carries `declined: true` and reads "None" instead.
   */
  /** A team's pill, with the urgent mark when the member holds no role in it (the message on hover). */
  const renderTeamChips = (items: string[]) => (
    <Box className="profileChipRow">
      {items.map((item, index) =>
        flaggedTeams.has(item) ? (
          <DosenTooltip key={`${item}-${index}`} title={NO_ROLE_MESSAGE} placement="top">
            <Box data-testid="no-role-flag" sx={{ position: 'relative', display: 'inline-flex' }}>
              <MyChip
                label={item}
                color="var(--Text-text-primary, #FDFDFD)"
                backgroundColor="transparent"
                border="1px solid #E5484D"
                fontSize={12}
              />
              <Box
                aria-label={NO_ROLE_MESSAGE}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: '#E5484D',
                  border: '2px solid #1C1C1C',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <PriorityHighIcon sx={{ fontSize: 10, color: '#FDFDFD' }} />
              </Box>
            </Box>
          </DosenTooltip>
        ) : (
          <MyChip
            key={`${item}-${index}`}
            label={item}
            color="var(--Text-text-primary, #FDFDFD)"
            backgroundColor="transparent"
            border="1px solid var(--Stroke-Border-border, #444444)"
            fontSize={12}
          />
        )
      )}
    </Box>
  );

  const renderMemberAttributes = (rows: MemberAttributeProfileRow[]) => {
    if (rows.length === 0) {
      return <Typography className="profilePlaceholderText">Not added yet</Typography>;
    }
    return (
      <Box className="profileAttributeRows">
        {rows.map((row) => (
          <Box className="profileAttributeRow" key={row.listId || row.listName}>
            <Typography className="profileAttributeName">{row.listName}</Typography>
            {row.values.length > 0 ? (
              row.listId === memberTeamRoles?.teamsListId ? (
                renderTeamChips(row.values)
              ) : (
                renderChips(row.values, 'Not added yet')
              )
            ) : (
              <Typography className="profilePlaceholderText">{row.declined ? 'None' : 'Not added yet'}</Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  if (showLoader) {
    return <ProfilePageLoader />;
  }

  if (userDetails.error) {
    return <Box>{userDetails.errorText}</Box>;
  }

  if (!userDetails.data || !data) {
    return null;
  }

  const displayName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Not added yet';
  const initials = (data.firstName || data.lastName || '')
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <>
      {!isMobileApp && mobileBackBtn('home')}
      <Box className="profilePageLayout">
        <Box className="profileRow">
          <ProfileCard
            title="Personal information"
            action={
              <IconButton
                className="profileCardActionButton"
                size="small"
                onClick={() => setEditProfileOpen(true)}
              >
                <EditIcon />
              </IconButton>
            }
          >
            <Box className="profileInfoSection">
              <ProfileAvatar headshot={data.headshot} initials={initials || 'B'} />
              <Box className="profileInfoDetails">
                <Typography className="profileInfoName">{displayName}</Typography>
                <Typography className="profileBodyText">{data.email || 'Not added yet'}</Typography>
                <Typography className="profileBodyText">
                  {data.phoneNumber ? `${data.phonePrefix} ${data.phoneNumber}` : '-/-'}
                </Typography>
                <Typography className="profileBodyText">
                  {data.country ? `${data.country} (${data.timezone})` : '-/-'}
                </Typography>
                <Box className="profileInfoTags">
                  <RoleTypeTag category={data.category} place="profile" />
                </Box>
              </Box>
            </Box>
          </ProfileCard>

          <ProfileCard
            title="My attributes"
            action={
              <IconButton
                className="profileCardActionButton"
                size="small"
                onClick={() => setMemberAttributesModalOpen(true)}
              >
                <EditIcon />
              </IconButton>
            }
          >
            {renderMemberAttributes(memberAttributeRows)}
          </ProfileCard>
        </Box>

        {/* Bio, across the page. */}
        <Box className="profileRow">
          <ProfileCard
            title="Bio"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={() => setEditProfileOpen(true)}>
                <EditIcon />
              </IconButton>
            }
          >
            <Typography className="profileBodyText">{userBio || 'Not added yet'}</Typography>
          </ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard
            title="Work experience"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleAddWorkHistory}>
                <EditIcon />
              </IconButton>
            }
          >
            <WorkAndEduDesign data={workHistoryData} itemType="work" wherePrint="profile" onEdit={handleEditWorkHistory} />
          </ProfileCard>

          <ProfileCard
            title="Education and skills"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleAddEducation}>
                <EditIcon />
              </IconButton>
            }
          >
            <WorkAndEduDesign data={educationData} itemType="education" wherePrint="profile" onEdit={handleEditEducation} />
          </ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard
            title="Video introduction"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenDigital}>
                <EditIcon />
              </IconButton>
            }
          >
            {videoIntro ? (
              <VideoPlayer src={videoIntro} width={forMobile ? '100%' : '360px'} height={forMobile ? '150px' : '220px'} />
            ) : (
              <Box>
                <Typography className="profileBodyText">
                  Video introductions will allow you to introduce yourself to your community and build a network.
                </Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={handleOpenDigital}>
                  Add
                </Button>
              </Box>
            )}
          </ProfileCard>

          <ProfileCard
            title="Social handles"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenSocial}>
                <EditIcon />
              </IconButton>
            }
          >
            <Box className="profileSocialRow">
              <GetSocialIcon data={data.social || {}} location="profile" />
            </Box>
          </ProfileCard>
        </Box>

        <Box className="profileRow">
          <ProfileCard
            title="Disability"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenDisability}>
                <EditIcon />
              </IconButton>
            }
          >
            <Typography className="profileBodyText">
              {data.disability && data.disability.length > 0 ? data.disability.join(', ') : 'No Disabilities'}
            </Typography>
          </ProfileCard>

          <ProfileCard
            title="Hobbies and interests"
            action={
              <IconButton className="profileCardActionButton" size="small" onClick={handleOpenHobbies}>
                <EditIcon />
              </IconButton>
            }
          >
            {renderChips(data.hobbies || [], 'Not added yet')}
          </ProfileCard>
        </Box>
      </Box>
      <EditProfile open={editProfileOpen} onClose={() => setEditProfileOpen(false)} />
        <DisabilityModal />
        <SocialHandlesModal />
        <HobbiesModal />
        <DigitalIntroModal />
        <EducationModal />
        <WorkHistoryModal />
        <MemberAttributesModal
          open={memberAttributesModalOpen}
          onClose={() => setMemberAttributesModalOpen(false)}
          orgMemberAttributes={
            (userDetails.data?.orgMemberAttributes?.length
              ? userDetails.data.orgMemberAttributes
              : fetchedOrgMemberAttributes) ?? []
          }
          selectedIds={userDetails.data?.memberAttributes ?? []}
          declinedListIds={userDetails.data?.memberAttributesDeclinedLists ?? []}
          teamRoles={memberTeamRoles}
          onSave={(memberAttributes, declinedListIds) => {
            dispatch(userMemberAttributesUpdate(memberAttributes));
            dispatch(userMemberAttributesChangedUpdate(false));
            dispatch(
              userMemberAttributesAckUpdate(
                catalogValueIds(
                  userDetails.data?.orgMemberAttributes?.length
                    ? userDetails.data.orgMemberAttributes
                    : fetchedOrgMemberAttributes
                )
              )
            );
            dispatch(userMemberAttributesDeclinedUpdate(declinedListIds));
          }}
        />
    </>
  );
};

export default Profile;
